
import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { articlesData } from '../../mocks/articles';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [isLoaded, setIsLoaded] = useState(false);

  const article = id ? articlesData[parseInt(id) as keyof typeof articlesData] : null;

  useEffect(() => {
    if (!article) {
      navigate('/');
      return;
    }

    // Quick fade-in animation
    setTimeout(() => setIsLoaded(true), 100);
  }, [article, navigate]);

  if (!article) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gray-900">
      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 bg-gray-900/95 backdrop-blur-md border-b border-gray-700 transition-all duration-500 ${isLoaded ? 'translate-y-0' : '-translate-y-full'}`}>
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <button
            onClick={() => navigate('/')}
            className="flex items-center text-gray-400 hover:text-green-400 transition-colors duration-300 cursor-pointer hover-scale"
          >
            <i className="ri-arrow-left-line mr-2"></i>
            Back to Portfolio
          </button>
          
          <div className="flex items-center space-x-4">
            <button className="text-gray-500 hover:text-green-400 transition-colors duration-300 cursor-pointer">
              <i className="ri-bookmark-line text-xl"></i>
            </button>
            <button className="text-gray-500 hover:text-green-400 transition-colors duration-300 cursor-pointer">
              <i className="ri-share-line text-xl"></i>
            </button>
          </div>
        </div>
      </nav>

      {/* Article Content */}
      <main className="pt-20">
        {/* Article Header */}
        <header className={`py-16 px-4 transition-all duration-700 ${isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <div className="max-w-4xl mx-auto">
            <div className="text-center">
              {/* Category */}
              <div className="inline-block bg-green-500/20 text-green-400 px-4 py-2 rounded-full text-sm font-medium mb-6 border border-green-500/30">
                {article.category}
              </div>
              
              {/* Title */}
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
                {article.title}
              </h1>
              
              {/* Subtitle */}
              <p className="text-xl md:text-2xl text-gray-300 leading-relaxed max-w-3xl mx-auto mb-8">
                {article.excerpt}
              </p>

              {/* Author Info */}
              <div className="flex items-center justify-center space-x-6 text-gray-400 text-sm mb-8">
                <div className="flex items-center">
                  <div className="w-10 h-10 bg-green-500 rounded-full flex items-center justify-center text-white font-bold mr-3">
                    {article.author.charAt(0)}
                  </div>
                  <span className="font-medium text-gray-300">{article.author}</span>
                </div>
                <div className="flex items-center">
                  <i className="ri-calendar-line mr-2"></i>
                  <span>{new Date(article.date).toLocaleDateString('en-US', { 
                    year: 'numeric', 
                    month: 'long', 
                    day: 'numeric' 
                  })}</span>
                </div>
                <div className="flex items-center">
                  <i className="ri-time-line mr-2"></i>
                  <span>{article.readTime}</span>
                </div>
              </div>

              {/* Tags */}
              <div className="flex flex-wrap gap-2 justify-center">
                {article.tags.map((tag, index) => (
                  <span
                    key={index}
                    className="bg-gray-800 text-gray-300 px-3 py-1 rounded-full text-sm hover:bg-gray-700 transition-colors duration-300 border border-gray-700"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </header>

        {/* Featured Image */}
        <section className={`px-4 mb-16 transition-all duration-700 ${isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`} style={{ transitionDelay: '0.1s' }}>
          <div className="max-w-5xl mx-auto">
            <div className="rounded-2xl overflow-hidden shadow-2xl border border-gray-700">
              <img
                src={article.image}
                alt={article.title}
                className="w-full h-64 md:h-96 object-cover object-top hover:scale-105 transition-transform duration-500"
              />
            </div>
          </div>
        </section>

        {/* Article Content */}
        <article className={`px-4 pb-16 transition-all duration-700 ${isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`} style={{ transitionDelay: '0.2s' }}>
          <div className="max-w-4xl mx-auto">
            <div className="prose prose-lg prose-invert max-w-none">
              <div className="text-gray-200 leading-relaxed space-y-6">
                {article.content.split('\n\n').map((paragraph, index) => {
                  // Handle headings
                  if (paragraph.startsWith('# ')) {
                    return (
                      <h1 key={index} className="text-4xl font-bold text-white mt-12 mb-6 first:mt-0">
                        {paragraph.replace('# ', '')}
                      </h1>
                    );
                  }
                  if (paragraph.startsWith('## ')) {
                    return (
                      <h2 key={index} className="text-3xl font-bold text-white mt-10 mb-5">
                        {paragraph.replace('## ', '')}
                      </h2>
                    );
                  }
                  if (paragraph.startsWith('### ')) {
                    return (
                      <h3 key={index} className="text-2xl font-bold text-green-400 mt-8 mb-4">
                        {paragraph.replace('### ', '')}
                      </h3>
                    );
                  }
                  
                  // Handle code blocks
                  if (paragraph.includes('```')) {
                    const codeMatch = paragraph.match(/```(\w+)?\n([\s\S]*?)```/);
                    if (codeMatch) {
                      return (
                        <div key={index} className="my-8">
                          <div className="bg-gray-800 border border-gray-700 rounded-lg overflow-hidden">
                            <div className="bg-gray-900 px-4 py-2 border-b border-gray-700">
                              <span className="text-green-400 text-sm font-medium">
                                {codeMatch[1] || 'code'}
                              </span>
                            </div>
                            <pre className="p-4 overflow-x-auto">
                              <code className="text-gray-200 text-sm font-mono leading-relaxed">
                                {codeMatch[2]}
                              </code>
                            </pre>
                          </div>
                        </div>
                      );
                    }
                  }
                  
                  // Handle numbered lists
                  if (/^\d+\./.test(paragraph.trim())) {
                    const listItems = paragraph.split('\n').filter(line => /^\d+\./.test(line.trim()));
                    return (
                      <ol key={index} className="list-decimal list-inside space-y-2 text-gray-200 my-6">
                        {listItems.map((item, itemIndex) => (
                          <li key={itemIndex} className="leading-relaxed">
                            {item.replace(/^\d+\.\s*/, '')}
                          </li>
                        ))}
                      </ol>
                    );
                  }
                  
                  // Handle bullet lists
                  if (paragraph.includes('- **') || paragraph.includes('* **')) {
                    const listItems = paragraph.split('\n').filter(line => line.trim().startsWith('- ') || line.trim().startsWith('* '));
                    return (
                      <ul key={index} className="list-disc list-inside space-y-2 text-gray-200 my-6">
                        {listItems.map((item, itemIndex) => {
                          const cleanItem = item.replace(/^[-*]\s*/, '');
                          // Handle bold text
                          const formattedItem = cleanItem.replace(/\*\*(.*?)\*\*/g, '<strong class="text-green-400 font-semibold">$1</strong>');
                          return (
                            <li key={itemIndex} className="leading-relaxed" dangerouslySetInnerHTML={{ __html: formattedItem }} />
                          );
                        })}
                      </ul>
                    );
                  }
                  
                  // Handle regular paragraphs
                  if (paragraph.trim() && !paragraph.startsWith('#') && !paragraph.includes('```')) {
                    // Handle inline code
                    let formattedParagraph = paragraph.replace(/`([^`]+)`/g, '<code class="bg-gray-800 text-green-400 px-2 py-1 rounded text-sm font-mono">$1</code>');
                    // Handle bold text
                    formattedParagraph = formattedParagraph.replace(/\*\*(.*?)\*\*/g, '<strong class="text-green-400 font-semibold">$1</strong>');
                    
                    return (
                      <p key={index} className="text-lg leading-relaxed text-gray-200 mb-6" dangerouslySetInnerHTML={{ __html: formattedParagraph }} />
                    );
                  }
                  
                  return null;
                })}
              </div>
            </div>
          </div>
        </article>

        {/* Article Footer */}
        <footer className={`px-4 pb-16 transition-all duration-700 ${isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`} style={{ transitionDelay: '0.3s' }}>
          <div className="max-w-4xl mx-auto">
            {/* Divider */}
            <div className="border-t border-gray-700 pt-12 mb-12"></div>
            
            {/* Author Card */}
            <div className="bg-gray-800/50 border border-gray-700 rounded-2xl p-8 mb-12 backdrop-blur-sm">
              <div className="flex items-start space-x-4">
                <div className="w-16 h-16 bg-green-500 rounded-full flex items-center justify-center text-white font-bold text-xl flex-shrink-0">
                  {article.author.charAt(0)}
                </div>
                <div>
                  <h3 className="text-xl font-bold text-white mb-2">{article.author}</h3>
                  <p className="text-gray-300 leading-relaxed">
                    Passionate about systems programming, performance optimization, and building scalable software solutions. 
                    Sharing insights from years of experience in high-performance computing and software architecture.
                  </p>
                  <div className="flex space-x-4 mt-4">
                    <button className="text-gray-500 hover:text-green-400 transition-colors duration-300">
                      <i className="ri-twitter-line text-xl"></i>
                    </button>
                    <button className="text-gray-500 hover:text-green-400 transition-colors duration-300">
                      <i className="ri-linkedin-line text-xl"></i>
                    </button>
                    <button className="text-gray-500 hover:text-green-400 transition-colors duration-300">
                      <i className="ri-github-line text-xl"></i>
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Share Section */}
            <div className="text-center mb-12">
              <h3 className="text-lg font-semibold text-white mb-4">Share this article</h3>
              <div className="flex justify-center space-x-4">
                <button className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg transition-colors duration-300 flex items-center">
                  <i className="ri-twitter-line mr-2"></i>
                  Twitter
                </button>
                <button className="bg-blue-800 hover:bg-blue-900 text-white px-6 py-3 rounded-lg transition-colors duration-300 flex items-center">
                  <i className="ri-linkedin-line mr-2"></i>
                  LinkedIn
                </button>
                <button className="bg-gray-700 hover:bg-gray-600 text-white px-6 py-3 rounded-lg transition-colors duration-300 flex items-center">
                  <i className="ri-github-line mr-2"></i>
                  GitHub
                </button>
              </div>
            </div>

            {/* Navigation */}
            <div className="flex items-center justify-between">
              <button
                onClick={() => navigate('/')}
                className="bg-green-500 hover:bg-green-600 text-white px-8 py-3 rounded-lg transition-all duration-300 hover:shadow-lg flex items-center cursor-pointer hover-scale"
              >
                <i className="ri-arrow-left-line mr-2"></i>
                Back to Articles
              </button>

              <button className="bg-gray-700 hover:bg-gray-600 text-white px-8 py-3 rounded-lg transition-all duration-300 hover:shadow-lg flex items-center cursor-pointer hover-scale">
                Next Article
                <i className="ri-arrow-right-line ml-2"></i>
              </button>
            </div>
          </div>
        </footer>
      </main>
    </div>
  );
}
